"use client"

import { useEffect, useRef, useState } from "react"
import type { LevelConfig, Position, ViewportState } from "@/types/game.types"
import { gameSettings, VIEWPORT_WIDTH, VIEWPORT_HEIGHT, WORLD_WIDTH } from "@/data/game-settings"
import { drawPath, drawBase, drawLevelTilemap } from "@/utils/rendering"
import { useViewport } from "@/contexts/viewport-context"
import { playDroppodLandSound } from "@/hooks/use-sound-effects"

interface LandingSequenceProps {
  level: LevelConfig
  onComplete: (landingPosition: Position) => void
  tileSize?: number
}

const LANDING_DURATION = 1000
const SMOKE_DURATION = 1500
const ZOOM_MAX = 2.0
const ZOOM_DEFAULT = 1.0
const ZOOM_OUT = gameSettings.canvas.viewportHeight / gameSettings.canvas.height

interface SmokeParticle {
  id: number
  x: number
  y: number
  size: number
  opacity: number
  vx: number
  vy: number
}

interface SpeedLine {
  x: number
  y: number
  length: number
  speed: number
  opacity: number
}

export const LandingSequence = ({ level, onComplete, tileSize: propTileSize }: LandingSequenceProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [phase, setPhase] = useState<"descending" | "landed" | "heroExit" | "zoomOut">("descending")
  const [podPosition, setPodPosition] = useState({ x: 0, y: -100 })
  const [podScale, setPodScale] = useState(0.3)

  // Get viewport context for desktop detection
  const { isDesktop, isPillarboxed } = useViewport()
  // Track actual canvas dimensions for viewport calculations
  const [canvasDimensions, setCanvasDimensions] = useState({ width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT })
  // Keep a ref for dimensions that animation callbacks can access
  const canvasDimensionsRef = useRef({ width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT })

  const [viewport, setViewport] = useState<ViewportState>({
    x: 0,
    y: 0,
    width: VIEWPORT_WIDTH,
    height: VIEWPORT_HEIGHT,
    zoom: ZOOM_OUT,
  })
  const [smokeParticles, setSmokeParticles] = useState<SmokeParticle[]>([])
  const [speedLines, setSpeedLines] = useState<SpeedLine[]>([])
  const [screenShake, setScreenShake] = useState({ x: 0, y: 0 })

  const startTimeRef = useRef<number>(0)
  const animationRef = useRef<number | null>(null)
  const particleIdRef = useRef(0)
  const podImageRef = useRef<HTMLImageElement | null>(null)
  const landedPodImageRef = useRef<HTMLImageElement | null>(null)
  const smokeIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const onCompleteRef = useRef(onComplete)
  const hasCompletedRef = useRef(false)
  const hasStartedRef = useRef(false)
  const hasPlayedLandSoundRef = useRef(false)

  // Keep ref updated but don't trigger re-renders
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  const tileSize = propTileSize || gameSettings.canvas.tileSize
  const landingTile = level?.landingTile || { x: 9, y: 15 }

  const landingXRef = useRef(landingTile.x * tileSize + tileSize / 2)
  const landingYRef = useRef(landingTile.y * tileSize + tileSize / 2)

  // Update refs when values change
  landingXRef.current = landingTile.x * tileSize + tileSize / 2
  landingYRef.current = landingTile.y * tileSize + tileSize / 2

  // Preload images
  useEffect(() => {
    const podImg = new window.Image()
    podImg.src = "/images/droppod.png"
    podImg.crossOrigin = "anonymous"
    podImageRef.current = podImg

    const landedImg = new window.Image()
    landedImg.src = "/images/droppod-landed.png"
    landedImg.crossOrigin = "anonymous"
    landedPodImageRef.current = landedImg
  }, [])

  // Initialize speed lines - only once
  useEffect(() => {
    const lines: SpeedLine[] = []
    for (let i = 0; i < 30; i++) {
      lines.push({
        x: Math.random() * gameSettings.canvas.viewportWidth,
        y: Math.random() * gameSettings.canvas.viewportHeight,
        length: 50 + Math.random() * 100,
        speed: 300 + Math.random() * 400,
        opacity: 0.3 + Math.random() * 0.5,
      })
    }
    setSpeedLines(lines)
  }, [])

  useEffect(() => {
    // Prevent re-running if already started
    if (hasStartedRef.current) return
    hasStartedRef.current = true

    // Every timer started below is tracked so cleanup can cancel the whole
    // chain. Without this a StrictMode remount (dev) cancels the RAF but leaves
    // the guard set, and the sequence never reaches onComplete.
    const timeouts: ReturnType<typeof setTimeout>[] = []
    const later = (fn: () => void, ms: number) => {
      timeouts.push(setTimeout(fn, ms))
    }

    const landingX = landingXRef.current
    const landingY = landingYRef.current

    const updateViewportCenteredOn = (x: number, y: number, zoom: number) => {
      // Use ref to get current canvas dimensions (may be wider on desktop)
      const currentWidth = canvasDimensionsRef.current.width
      const currentHeight = canvasDimensionsRef.current.height
      const viewWidth = currentWidth / zoom
      const viewHeight = currentHeight / zoom
      const viewX = Math.max(0, Math.min(gameSettings.canvas.width - viewWidth, x - viewWidth / 2))
      const viewY = Math.max(0, Math.min(gameSettings.canvas.height - viewHeight, y - viewHeight / 2))

      setViewport({
        x: viewX,
        y: viewY,
        width: currentWidth,
        height: currentHeight,
        zoom: zoom,
      })
    }

    const triggerScreenShake = () => {
      const startTime = performance.now()
      const shakeDuration = 500

      const shake = (time: number) => {
        const elapsed = time - startTime
        const remaining = 1 - elapsed / shakeDuration

        if (remaining > 0) {
          const intensity = remaining * 8
          setScreenShake({
            x: (Math.random() - 0.5) * intensity,
            y: (Math.random() - 0.5) * intensity,
          })
          requestAnimationFrame(shake)
        } else {
          setScreenShake({ x: 0, y: 0 })
        }
      }

      requestAnimationFrame(shake)
    }

    const startSmokeEffect = () => {
      const createParticle = (): SmokeParticle => ({
        id: particleIdRef.current++,
        x: landingX + (Math.random() - 0.5) * 30,
        y: landingY,
        size: 8 + Math.random() * 16,
        opacity: 0.6 + Math.random() * 0.4,
        vx: (Math.random() - 0.5) * 20,
        vy: -30 - Math.random() * 40,
      })

      const initialParticles: SmokeParticle[] = []
      for (let i = 0; i < 15; i++) {
        initialParticles.push(createParticle())
      }
      setSmokeParticles(initialParticles)

      smokeIntervalRef.current = setInterval(() => {
        setSmokeParticles((prev) => {
          const updated = prev
            .map((p) => ({
              ...p,
              x: p.x + p.vx * 0.03,
              y: p.y + p.vy * 0.03,
              opacity: p.opacity - 0.015,
              size: p.size + 0.3,
            }))
            .filter((p) => p.opacity > 0)

          if (updated.length < 20) {
            updated.push(createParticle())
          }

          return updated
        })
      }, 50)

      later(() => {
        if (smokeIntervalRef.current) {
          clearInterval(smokeIntervalRef.current)
        }
      }, SMOKE_DURATION)
    }

    const startZoomOutAnimation = () => {
      if (hasCompletedRef.current) return

      const startTime = performance.now()
      const zoomDuration = 250

      const animateZoomOut = (time: number) => {
        const elapsed = time - startTime
        const progress = Math.min(elapsed / zoomDuration, 1)
        const easeOut = 1 - Math.pow(1 - progress, 3)

        const currentZoom = ZOOM_MAX + (ZOOM_DEFAULT - ZOOM_MAX) * easeOut
        updateViewportCenteredOn(landingX, landingY, currentZoom)

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animateZoomOut)
        } else {
          if (!hasCompletedRef.current) {
            hasCompletedRef.current = true
            onCompleteRef.current({ x: landingX, y: landingY })
          }
        }
      }

      animationRef.current = requestAnimationFrame(animateZoomOut)
    }

    startTimeRef.current = performance.now()

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTimeRef.current
      const normalizedProgress = Math.min(elapsed / LANDING_DURATION, 1)

      const easeOutQuart = 1 - Math.pow(1 - normalizedProgress, 4)
      const currentZoom = ZOOM_OUT + (ZOOM_MAX - ZOOM_OUT) * easeOutQuart

      const startY = -100
      const currentY = startY + (landingY - startY) * easeOutQuart
      const currentScale = 0.3 + (1 - 0.3) * easeOutQuart

      setPodPosition({ x: landingX, y: currentY })
      setPodScale(currentScale)

      updateViewportCenteredOn(landingX, currentY, currentZoom)

      setSpeedLines((prev) =>
        prev.map((line) => {
          const speedMultiplier = 1 + normalizedProgress * 2
          let newY = line.y + line.speed * speedMultiplier * 0.016
          if (newY > gameSettings.canvas.viewportHeight + line.length) {
            newY = -line.length
          }
          return { ...line, y: newY, opacity: line.opacity * (1 - normalizedProgress * 0.5) }
        }),
      )

      // Play land sound 300ms before screen shake (at 70% progress)
      if (normalizedProgress >= 0.7 && !hasPlayedLandSoundRef.current) {
        hasPlayedLandSoundRef.current = true
        playDroppodLandSound()
      }

      if (normalizedProgress < 1) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        setPhase("landed")
        triggerScreenShake()
        startSmokeEffect()

        later(() => {
          setPhase("heroExit")
          later(() => {
            setPhase("zoomOut")
            startZoomOutAnimation()
          }, 250)
        }, 250)
      }
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      if (smokeIntervalRef.current) {
        clearInterval(smokeIntervalRef.current)
      }
      for (const t of timeouts) clearTimeout(t)
      // Allow the sequence to start over if this was a remount rather than a
      // real teardown (React StrictMode mounts, cleans up, then mounts again).
      if (!hasCompletedRef.current) {
        hasStartedRef.current = false
      }
    }
  }, []) // Empty dependency array - run only once on mount

  // Render canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const landingX = landingXRef.current
    const landingY = landingYRef.current

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    ctx.save()
    ctx.translate(screenShake.x, screenShake.y)

    ctx.save()
    ctx.scale(viewport.zoom, viewport.zoom)
    ctx.translate(-viewport.x, -viewport.y)

    const renderWidth = viewport.width / viewport.zoom
    const renderHeight = viewport.height / viewport.zoom

    // Draw the real level ground during the descent - this used to be a flat
    // colour + drawn path line, so the map visibly "switched" to the tiled
    // terrain only when gameplay started
    if (level.tilemap) {
      drawLevelTilemap(ctx, level.tilemap, viewport.x, viewport.y, renderWidth, renderHeight)
    } else {
      ctx.fillStyle = gameSettings.colors.background
      ctx.fillRect(viewport.x, viewport.y, renderWidth, renderHeight)
      drawPath(ctx, level.path, level.pathWidth)
    }
    drawBase(ctx, level.basePosition, level.path)

    if (phase === "landed" || phase === "heroExit" || phase === "zoomOut") {
      for (const particle of smokeParticles) {
        ctx.beginPath()
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(40, 40, 40, ${particle.opacity})`
        ctx.fill()
      }

      if (landedPodImageRef.current && landedPodImageRef.current.complete) {
        const podSize = 64
        ctx.drawImage(landedPodImageRef.current, landingX - podSize / 2, landingY - podSize / 2, podSize, podSize)
      }
    } else {
      if (podImageRef.current && podImageRef.current.complete) {
        const podSize = 64 * podScale
        ctx.drawImage(podImageRef.current, podPosition.x - podSize / 2, podPosition.y - podSize / 2, podSize, podSize)
      }
    }

    ctx.restore()

    if (phase === "descending") {
      ctx.strokeStyle = "rgba(255, 255, 255, 0.3)"
      ctx.lineWidth = 2
      for (const line of speedLines) {
        ctx.globalAlpha = line.opacity
        ctx.beginPath()
        ctx.moveTo(line.x, line.y)
        ctx.lineTo(line.x, line.y + line.length)
        ctx.stroke()
      }
      ctx.globalAlpha = 1
    }

    ctx.restore()
  }, [viewport, level, podPosition, podScale, phase, smokeParticles, speedLines, screenShake])

  // Resize canvas
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const resizeCanvas = () => {
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      const isDesktopLandscape = isDesktop && isPillarboxed

      if (isDesktopLandscape) {
        // Full-width mode on desktop landscape (same as game-canvas.tsx)
        const aspectRatio = screenWidth / screenHeight
        let internalWidth = Math.floor(VIEWPORT_HEIGHT * aspectRatio)

        // Cap internal width at WORLD_WIDTH to prevent showing empty space beyond the world
        if (internalWidth > WORLD_WIDTH) {
          internalWidth = WORLD_WIDTH
          const canvasDisplayWidth = screenHeight * (internalWidth / VIEWPORT_HEIGHT)
          canvas.style.width = `${canvasDisplayWidth}px`
        } else {
          canvas.style.width = `${screenWidth}px`
        }

        canvas.style.height = `${screenHeight}px`
        canvas.width = internalWidth
        canvas.height = VIEWPORT_HEIGHT
        setCanvasDimensions({ width: internalWidth, height: VIEWPORT_HEIGHT })
        canvasDimensionsRef.current = { width: internalWidth, height: VIEWPORT_HEIGHT }
      } else {
        // Original behavior: maintain aspect ratio with letterboxing
        const targetAspectRatio = VIEWPORT_WIDTH / VIEWPORT_HEIGHT
        const containerAspectRatio = screenWidth / screenHeight

        let scale
        if (containerAspectRatio > targetAspectRatio) {
          scale = screenHeight / VIEWPORT_HEIGHT
        } else {
          scale = screenWidth / VIEWPORT_WIDTH
        }

        canvas.style.width = `${VIEWPORT_WIDTH * scale}px`
        canvas.style.height = `${VIEWPORT_HEIGHT * scale}px`
        canvas.width = VIEWPORT_WIDTH
        canvas.height = VIEWPORT_HEIGHT
        setCanvasDimensions({ width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT })
        canvasDimensionsRef.current = { width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT }
      }
    }

    resizeCanvas()
    window.addEventListener("resize", resizeCanvas)
    return () => window.removeEventListener("resize", resizeCanvas)
  }, [isDesktop, isPillarboxed])

  return (
    <div ref={containerRef} className="fixed inset-0 bg-background flex items-center justify-center z-50">
      <canvas
        ref={canvasRef}
        width={canvasDimensions.width}
        height={canvasDimensions.height}
        style={{ imageRendering: "pixelated" }}
      />
    </div>
  )
}
